import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import featureFlags from '../lib/featureFlags';
import { meFn } from '../lib/api';
import {
    MARKETPLACE_COHORT_SETTING_KEY,
    MARKETPLACE_EXPERIMENT_SETTING_KEY,
    isMarketplaceCohort,
    isMarketplaceExperimentFlag,
    publishMarketplaceCartEnabled,
    resolveMarketplaceCohort,
    setMarketplaceCohort,
} from '../lib/marketplaceExperiment';
import { useUserContext } from './UserContext';
import { getCookie } from '../lib/utils';
import config from '../lib/config';

const MarketplaceExperimentContext = createContext({
    marketplaceEnabled: false,
    resolvedCohort: null,
    isResolved: false,
    promoteMarketplaceToB: async () => false,
});

const getSafeUserSettings = (user) => {
    return user?.settings && typeof user.settings === 'object' ? user.settings : {};
};

const MarketplaceExperimentProvider = ({ children }) => {
    const { user, isAuthenticating, setUser } = useUserContext();
    const [resolvedCohort, setResolvedCohort] = useState(() => resolveMarketplaceCohort());
    const [isResolved, setIsResolved] = useState(false);
    const [debugControl, setDebugControl] = useState(null);
    const [handoffCohort, setHandoffCohort] = useState(null);
    const lastSettingsSyncKeyRef = useRef(null);

    const promoteMarketplaceToB = useCallback(async () => {
        if (isAuthenticating) {
            throw new Error('Marketplace authentication is still resolving');
        }

        const cohort = setMarketplaceCohort('B');
        if (cohort !== 'B') {
            throw new Error('Unable to persist marketplace cohort');
        }

        setHandoffCohort('B');
        setResolvedCohort('B');
        setIsResolved(true);

        const settings = getSafeUserSettings(user);
        const hasAccountCohort = settings[MARKETPLACE_COHORT_SETTING_KEY] === 'B';
        const hasExperimentFlag = isMarketplaceExperimentFlag(settings[MARKETPLACE_EXPERIMENT_SETTING_KEY]);

        if (!user?.id || (hasAccountCohort && hasExperimentFlag)) {
            return 'B';
        }

        const payload = {
            [MARKETPLACE_COHORT_SETTING_KEY]: 'B',
            [MARKETPLACE_EXPERIMENT_SETTING_KEY]: '1',
        };
        const response = await meFn(payload);

        if (!response) {
            throw new Error('Unable to promote marketplace cohort');
        }

        setUser((currentUser) => {
            if (!currentUser || currentUser.id !== user.id) {
                return currentUser;
            }

            return {
                ...currentUser,
                settings: {
                    ...getSafeUserSettings(currentUser),
                    ...payload,
                },
            };
        });

        return 'B';
    }, [isAuthenticating, setUser, user]);

    useEffect(() => {
        const refreshExperiment = (event) => {
            const detail = event?.detail;
            if (detail?.experimentId !== 'marketplace_v1') {
                return;
            }

            if (detail.cleared === true) {
                setDebugControl({ type: 'cleared' });
                return;
            }

            if (isMarketplaceCohort(detail.cohort)) {
                setDebugControl({
                    type: 'override',
                    cohort: detail.cohort.trim().toUpperCase(),
                });
            }
        };
        window.addEventListener('crate:experiment-updated', refreshExperiment);

        return () => window.removeEventListener('crate:experiment-updated', refreshExperiment);
    }, []);

    useEffect(() => {
        if (handoffCohort === 'B') {
            setResolvedCohort('B');
            setIsResolved(true);
            return;
        }

        if (debugControl?.type === 'cleared') {
            setResolvedCohort(null);
            setIsResolved(true);
            return;
        }

        if (debugControl?.type === 'override') {
            setResolvedCohort(debugControl.cohort);
            setIsResolved(true);
            return;
        }

        const browserCohort = resolveMarketplaceCohort();
        setResolvedCohort(browserCohort);

        if (isAuthenticating) {
            setIsResolved(false);
            return;
        }

        if (!user?.id) {
            setIsResolved(true);
            return;
        }

        const nextResolvedCohort = resolveMarketplaceCohort({ userSettings: user.settings });
        const settings = getSafeUserSettings(user);
        const hasAccountCohort = isMarketplaceCohort(settings[MARKETPLACE_COHORT_SETTING_KEY]);
        const hasExperimentFlag = isMarketplaceExperimentFlag(settings[MARKETPLACE_EXPERIMENT_SETTING_KEY]);
        const needsCohort = !hasAccountCohort || settings[MARKETPLACE_COHORT_SETTING_KEY] !== nextResolvedCohort;
        const needsExperiment = !hasExperimentFlag;

        setResolvedCohort(nextResolvedCohort);
        setIsResolved(true);

        if (!needsCohort && !needsExperiment) {
            return;
        }

        const syncKey = [
            user.id,
            nextResolvedCohort,
            settings[MARKETPLACE_COHORT_SETTING_KEY] ?? '',
            settings[MARKETPLACE_EXPERIMENT_SETTING_KEY] ?? '',
            needsCohort ? '1' : '0',
            needsExperiment ? '1' : '0',
        ].join(':');

        if (lastSettingsSyncKeyRef.current === syncKey) {
            return;
        }
        lastSettingsSyncKeyRef.current = syncKey;

        const needsMarketplaceSettingsSync = needsCohort || needsExperiment;
        const payload = {};
        if (needsMarketplaceSettingsSync) {
            payload[MARKETPLACE_COHORT_SETTING_KEY] = nextResolvedCohort;
            payload[MARKETPLACE_EXPERIMENT_SETTING_KEY] = '1';
        }

        meFn(payload)
            .then((response) => {
                if (!response) {
                    console.log('marketplace cohort settings update failed');
                    return;
                }

                setUser((currentUser) => {
                    if (!currentUser || currentUser.id !== user.id) {
                        return currentUser;
                    }

                    const currentSettings = getSafeUserSettings(currentUser);
                    return {
                        ...currentUser,
                        settings: {
                            ...currentSettings,
                            ...payload,
                        },
                    };
                });
            })
            .catch((error) => {
                console.log('marketplace cohort settings update failed', error);
            });
    }, [
        isAuthenticating,
        setUser,
        user,
        user?.id,
        user?.settings,
        user?.settings?.cohort_marketplace_v1,
        user?.settings?.cohort_marketplace_v1_experiment,
        debugControl,
        handoffCohort,
    ]);

    const marketplaceEnabled = Boolean(
        isResolved
        && featureFlags.marketplaceCart
        && resolvedCohort === 'B'
        && (user?.id || !getCookie(config.premiumAssetsSubscriptionCookieKey))
    );

    useEffect(() => {
        publishMarketplaceCartEnabled(isResolved ? marketplaceEnabled : null);
    }, [isResolved, marketplaceEnabled]);

    const value = useMemo(() => ({
        marketplaceEnabled,
        resolvedCohort,
        isResolved,
        promoteMarketplaceToB,
    }), [marketplaceEnabled, promoteMarketplaceToB, resolvedCohort, isResolved]);

    return (
        <MarketplaceExperimentContext.Provider value={value}>
            {children}
        </MarketplaceExperimentContext.Provider>
    );
};

const useMarketplaceExperiment = () => useContext(MarketplaceExperimentContext);

export { MarketplaceExperimentProvider, useMarketplaceExperiment };
